import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useProjectStore } from '../hooks/useProjectStore';
import { formationAPI, wellboreAPI, pressureAPI } from '../services/api';
import Formation3DView from '../components/formation/Formation3DView';

// ═══════════════════════════════════════════════
// FracSight Formation + Wellbore + Pressure
// Data loaded from backend API
// ═══════════════════════════════════════════════

// ── Casing program is engineering config, kept client-side ──
const CASING_PROGRAM = [
  { name: '导管', top: 0, bottom: 80, od: 20, bitSize: 26, color: '#7A8898' },
  { name: '表层套管', top: 0, bottom: 500, od: 13.375, bitSize: 17.5, color: '#6A7888' },
  { name: '技术套管', top: 0, bottom: 2800, od: 9.625, bitSize: 12.25, color: '#5A6878' },
  { name: '生产套管', top: 0, bottom: 4280, od: 5.5, bitSize: 8.5, color: '#4A5868' },
];

type Layer = {
  name: string; top: number; bottom: number; color: string; lith: string; target?: boolean;
  dipAngle?: number;     // 倾角 (°)
  dipAzimuth?: number;   // 倾向方位 (°), 0=N, 90=E
  extentX?: number;      // 东西延展 (m)
  extentY?: number;      // 南北延展 (m)
};
type AvoidanceZone = { name: string; top: number; bottom: number; type: string; severity: number };
type Station = { md: number; inc: number; azi: number; tvd: number; ns: number; ew: number };
type PressurePoint = { depth: number; pore: number; collapse: number; fracture: number };
type CaliperPoint = { depth: number; bitSize: number; caliper: number; washoutRate: number; washout: boolean; washoutSeverity: number };
type Anomaly = { top: number; bottom: number; maxRate: number; severity: number };

// ── Map backend payload → UI shapes ────────────
const LITH_ZH: Record<string, string> = {
  sandstone: '砂岩', shale: '页岩', limestone: '灰岩', mudstone: '泥岩',
  dolomite: '白云岩', conglomerate: '砾岩', coal: '煤层', other: '其他',
};
// 岩性配色: 亮色工业风, 按行业色系惯例 (砂岩=黄/泥岩=绿/页岩=青/灰岩=蓝/
// 白云岩=紫/砾岩=橙/煤=近黑)。与后端 seed_demo.py 的取色保持一致。
const LITH_COLOR: Record<string, string> = {
  砾岩: '#E8944A', 灰岩: '#4098D4', 白云岩: '#9B7EDE',
  泥岩: '#56B871', 页岩: '#2E9AA6', 砂岩: '#F2B233', 煤层: '#2F3640',
  其他: '#7E8CA0',
};

function mapLayers(raw: any[]): Layer[] {
  return (raw || []).map((r) => {
    const rawLith = r.lithology ?? r.lith ?? 'other';
    const lith = LITH_ZH[rawLith] ?? rawLith;
    return {
      name: r.name ?? r.formation_name ?? '未命名',
      top: Number(r.top_depth ?? r.top ?? 0),
      bottom: Number(r.bottom_depth ?? r.bottom ?? 0),
      color: r.color_code || r.color || LITH_COLOR[lith] || '#707078',
      lith,
      target: Boolean(r.is_target ?? r.target ?? false),
      dipAngle: r.dip_angle !== undefined ? Number(r.dip_angle) : undefined,
      dipAzimuth: r.dip_azimuth !== undefined ? Number(r.dip_azimuth) : undefined,
      extentX: r.extent_x !== undefined ? Number(r.extent_x) : undefined,
      extentY: r.extent_y !== undefined ? Number(r.extent_y) : undefined,
    };
  });
}

function mapAvoidance(raw: any[]): AvoidanceZone[] {
  return (raw || []).map((r) => ({
    name: r.name ?? '避障区',
    top: Number(r.top_depth ?? r.top ?? 0),
    bottom: Number(r.bottom_depth ?? r.bottom ?? 0),
    type: r.zone_type ?? r.type ?? 'fault',
    severity: Number(r.severity ?? 3),
  }));
}

function mapSurvey(raw: any[]): Station[] {
  return (raw || []).map((r) => ({
    md: Number(r.md ?? r.measured_depth ?? 0),
    inc: Number(r.inclination ?? r.inc ?? 0),
    azi: Number(r.azimuth ?? r.azi ?? 0),
    tvd: Number(r.tvd ?? r.true_vertical_depth ?? 0),
    ns: Number(r.ns ?? r.n_coord ?? r.north ?? 0),
    ew: Number(r.ew ?? r.e_coord ?? r.east ?? 0),
  })).sort((a, b) => a.md - b.md);
}

function mapPressure(raw: any[]): PressurePoint[] {
  return (raw || []).map((r) => ({
    depth: Number(r.depth ?? 0),
    pore: Number(r.pore_pressure ?? r.pore ?? 0),
    collapse: Number(r.collapse_pressure ?? r.collapse ?? 0),
    fracture: Number(r.fracture_pressure ?? r.fracture ?? 0),
  })).sort((a, b) => a.depth - b.depth);
}

// ── Caliper synthesised from layers + avoidance zones ──────────
function generateCaliperData(layers: Layer[], zones: AvoidanceZone[], maxDepth: number): CaliperPoint[] {
  const data: CaliperPoint[] = [];
  const inLith = (depth: number, liths: string[]) =>
    layers.some((l) => depth >= l.top && depth < l.bottom && liths.includes(l.lith));
  const inZone = (depth: number, types: string[]) =>
    zones.some((z) => depth >= z.top - 10 && depth <= z.bottom + 10 && types.includes(z.type));

  for (let depth = 0; depth < maxDepth; depth += 10) {
    let bitSize = 8.5;
    for (const c of CASING_PROGRAM) {
      if (depth <= c.bottom) { bitSize = c.bitSize; break; }
    }
    let caliper = bitSize;
    let washout = false;
    let washoutSeverity = 0;

    const inMudstone = inLith(depth, ['泥岩']);
    const inShale = inLith(depth, ['页岩']);
    const nearFault = inZone(depth, ['fault']);
    const nearHP = inZone(depth, ['high_pressure']);

    if (nearFault) {
      caliper = bitSize + 2 + Math.random() * 4;
      washout = true; washoutSeverity = 3;
    } else if (nearHP) {
      caliper = bitSize + 1.5 + Math.random() * 3;
      washout = true; washoutSeverity = 2;
    } else if (inMudstone) {
      caliper = bitSize + 0.5 + Math.random() * 2;
      washout = Math.random() > 0.3;
      washoutSeverity = washout ? 1 : 0;
    } else if (inShale) {
      caliper = bitSize + 0.3 + Math.random() * 1.2;
      washout = Math.random() > 0.5;
      washoutSeverity = washout ? 1 : 0;
    } else {
      caliper = Math.max(bitSize * 0.98, bitSize + (Math.random() - 0.3) * 0.5);
    }

    const washoutRate = ((caliper - bitSize) / bitSize) * 100;
    data.push({
      depth,
      bitSize: Math.round(bitSize * 100) / 100,
      caliper: Math.round(caliper * 100) / 100,
      washoutRate: Math.round(washoutRate * 10) / 10,
      washout, washoutSeverity,
    });
  }
  return data;
}

function detectAnomalies(caliperData: CaliperPoint[]): Anomaly[] {
  const anomalies: Anomaly[] = [];
  let current: Anomaly | null = null;
  for (const pt of caliperData) {
    if (pt.washoutRate > 10) {
      if (!current) current = { top: pt.depth, bottom: pt.depth, maxRate: pt.washoutRate, severity: pt.washoutSeverity };
      else {
        current.bottom = pt.depth;
        current.maxRate = Math.max(current.maxRate, pt.washoutRate);
        current.severity = Math.max(current.severity, pt.washoutSeverity);
      }
    } else if (current) {
      if (current.bottom - current.top >= 10) anomalies.push(current);
      current = null;
    }
  }
  if (current && current.bottom - current.top >= 10) anomalies.push(current);
  return anomalies;
}

const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF',
  grid: 'rgba(30,42,58,0.06)', text: '#1E2A3A',
  textSec: '#4F6680', textMuted: '#8598AD',
  well: '#4A5868', wellWall: '#3D5168',
  casing: '#2C3E50', casingInner: '#E8E4DA',
  washoutMild: '#D4A017', washoutMod: '#D4770C', washoutSevere: '#C0392B',
  target: '#8B3A2A', targetBg: 'rgba(139,58,42,0.06)', targetBorder: 'rgba(139,58,42,0.25)',
  pore: '#2874A6', collapse: '#B8860B', fracture: '#C0392B',
  anomalyBg: 'rgba(192,57,43,0.08)', anomalyBorder: 'rgba(192,57,43,0.3)',
  headerBg: '#FFFFFF', headerBorder: '#D6DAE0',
  statBg: '#FFFFFF', statBorder: '#ECEEF1',
  panelHeaderBg: '#F4F6F9', accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
};

type PanelProps = {
  currentDepth: number; // 当前钻进进度 (测深 MD)
  currentTVD: number;   // 钻头当前真垂深 (TVD), 用于一切深度轴裁剪
  viewRange: [number, number];
  layers: Layer[];
  zones: AvoidanceZone[];
  caliperData: CaliperPoint[];
  anomalies: Anomaly[];
  pressureData: PressurePoint[];
  trajectory: Station[];
};

// ── Formation + Wellbore Panel ─────────────────
function FormationPanel({ currentDepth, currentTVD, viewRange, layers, zones, caliperData, anomalies, trajectory }: PanelProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr; canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    const [vTop, vBottom] = viewRange;
    const scaleY = (d: number) => ((d - vTop) / (vBottom - vTop)) * H;
    const trackLeft = 55, trackWidth = W - 75;
    const wellCenterX = trackLeft + trackWidth * 0.42;
    const maxWellPx = 40;

    ctx.fillStyle = TH.canvasBg;
    ctx.fillRect(0, 0, W, H);

    layers.forEach((layer) => {
      const y1 = scaleY(layer.top), y2 = scaleY(layer.bottom);
      if (y2 < 0 || y1 > H) return;
      const cy1 = Math.max(0, y1), cy2 = Math.min(H, y2), lh = cy2 - cy1;
      ctx.fillStyle = layer.color; ctx.globalAlpha = 0.5;
      ctx.fillRect(trackLeft, cy1, trackWidth, lh);
      ctx.globalAlpha = 1.0;

      ctx.strokeStyle = 'rgba(0,0,0,0.05)'; ctx.lineWidth = 0.5;
      if (layer.lith === '灰岩' || layer.lith === '白云岩') {
        for (let i = cy1; i < cy2; i += 8) { ctx.beginPath(); ctx.moveTo(trackLeft, i); ctx.lineTo(trackLeft + trackWidth, i); ctx.stroke(); }
      } else if (layer.lith === '页岩') {
        for (let i = cy1; i < cy2; i += 5) { ctx.beginPath(); ctx.moveTo(trackLeft, i); ctx.lineTo(trackLeft + trackWidth * 0.3, i); ctx.moveTo(trackLeft + trackWidth * 0.4, i + 2); ctx.lineTo(trackLeft + trackWidth * 0.7, i + 2); ctx.stroke(); }
      } else if (layer.lith === '泥岩') {
        ctx.fillStyle = 'rgba(0,0,0,0.03)';
        for (let x = trackLeft; x < trackLeft + trackWidth; x += 14) {
          for (let y = cy1; y < cy2; y += 12) { ctx.beginPath(); ctx.arc(x + Math.random() * 4, y + Math.random() * 4, 1.5, 0, Math.PI * 2); ctx.fill(); }
        }
      }

      if (y1 >= 0 && y1 <= H) { ctx.strokeStyle = 'rgba(0,0,0,0.1)'; ctx.lineWidth = 0.5; ctx.beginPath(); ctx.moveTo(trackLeft, y1); ctx.lineTo(trackLeft + trackWidth, y1); ctx.stroke(); }
      if (layer.target) {
        ctx.strokeStyle = TH.target; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
        ctx.strokeRect(trackLeft + 1, cy1 + 1, trackWidth - 2, lh - 2); ctx.setLineDash([]);
        ctx.fillStyle = TH.target; ctx.font = 'bold 10px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
        ctx.fillText('★ 目标层', trackLeft + 6, cy1 + 14);
      }
      if (lh > 22 && !layer.target) { ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.font = '10px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif'; ctx.fillText(layer.name, trackLeft + 6, cy1 + 14); }
    });

    zones.forEach((zone) => {
      const y1 = scaleY(zone.top), y2 = scaleY(zone.bottom);
      if (y2 < 0 || y1 > H) return;
      const colors: Record<string, string> = { fault: TH.washoutSevere, high_pressure: TH.washoutMod, collapse: TH.washoutMild };
      const color = colors[zone.type] || TH.washoutMod;
      ctx.fillStyle = color; ctx.globalAlpha = 0.08;
      ctx.fillRect(trackLeft, Math.max(0, y1), trackWidth, y2 - y1);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
      ctx.strokeRect(trackLeft, Math.max(0, y1), trackWidth, y2 - y1);
      ctx.setLineDash([]);
      ctx.fillStyle = color; ctx.font = 'bold 9px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
      ctx.fillText(`⚠ ${zone.name}`, trackLeft + trackWidth - 80, Math.max(12, y1 + 12));
    });

    // ── Trajectory geometry (for horizontal wells) ──
    const horizOf = (s: Station) => Math.hypot(s.ns, s.ew);
    const maxHoriz = trajectory.length ? Math.max(...trajectory.map(horizOf)) : 0;
    const kopStation = trajectory.find((s) => s.inc > 5);
    const kopTVD = kopStation ? kopStation.tvd : Number.POSITIVE_INFINITY;
    const lateralPx = Math.max(0, trackWidth - (wellCenterX - trackLeft) - 80);
    const pxPerMeter = maxHoriz > 5 ? Math.min(lateralPx / maxHoriz, 0.15) : 0;
    const hasLateral = maxHoriz > 50;
    const verticalCap = hasLateral ? kopTVD : Number.POSITIVE_INFINITY;

    // Locate a casing shoe's screen position. For shoes whose bottom (MD value) is past
    // KOP in a horizontal well, place the shoe at the trajectory station with closest MD.
    const shoeXY = (csgBottomMd: number): { x: number; y: number } => {
      if (!hasLateral || csgBottomMd <= kopTVD) {
        return { x: wellCenterX, y: scaleY(csgBottomMd) };
      }
      // Find trajectory station closest to this MD
      let best = trajectory[0];
      let bestDiff = Math.abs(best.md - csgBottomMd);
      for (const s of trajectory) {
        const d = Math.abs(s.md - csgBottomMd);
        if (d < bestDiff) { best = s; bestDiff = d; }
      }
      return { x: wellCenterX + horizOf(best) * pxPerMeter, y: scaleY(best.tvd) };
    };

    CASING_PROGRAM.forEach((csg) => {
      // 实时展示: 只有钻进 (MD) 已下到该套管座深时才显示套管鞋+标签
      if (currentDepth < csg.bottom) return;
      const { x, y } = shoeXY(csg.bottom);
      if (y < 0 || y > H) return;
      ctx.strokeStyle = TH.casing; ctx.lineWidth = 1.5;
      const halfW = (csg.od / 26) * maxWellPx / 2;
      ctx.beginPath();
      ctx.moveTo(x - halfW - 4, y); ctx.lineTo(x - halfW, y + 6); ctx.lineTo(x - halfW, y);
      ctx.moveTo(x + halfW + 4, y); ctx.lineTo(x + halfW, y + 6); ctx.lineTo(x + halfW, y);
      ctx.stroke();
      ctx.fillStyle = TH.textSec; ctx.font = '9px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace';
      ctx.fillText(`${csg.od}"${csg.name}`, x + halfW + 8, y - 4);
    });

    const visibleCaliper = caliperData.filter((c) => c.depth <= currentTVD && c.depth >= vTop && c.depth <= vBottom && c.depth <= verticalCap);

    if (visibleCaliper.length > 1) {
      for (let i = 0; i < visibleCaliper.length - 1; i++) {
        const pt = visibleCaliper[i];
        const ptNext = visibleCaliper[i + 1];
        const y1 = scaleY(pt.depth);
        const y2 = scaleY(ptNext.depth);
        const halfW1 = (pt.caliper / 26) * maxWellPx / 2;
        const halfW2 = (ptNext.caliper / 26) * maxWellPx / 2;
        const halfBit1 = (pt.bitSize / 26) * maxWellPx / 2;

        let fillColor;
        if (pt.washoutSeverity >= 3) fillColor = 'rgba(192,57,43,0.25)';
        else if (pt.washoutSeverity >= 2) fillColor = 'rgba(212,119,12,0.2)';
        else if (pt.washoutSeverity >= 1) fillColor = 'rgba(212,160,23,0.15)';
        else fillColor = 'rgba(100,120,140,0.08)';

        ctx.fillStyle = fillColor;
        ctx.beginPath();
        ctx.moveTo(wellCenterX - halfW1, y1);
        ctx.lineTo(wellCenterX - halfW2, y2);
        ctx.lineTo(wellCenterX + halfW2, y2);
        ctx.lineTo(wellCenterX + halfW1, y1);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = pt.washoutSeverity >= 2 ? TH.washoutSevere : pt.washoutSeverity >= 1 ? TH.washoutMod : TH.wellWall;
        ctx.lineWidth = pt.washoutSeverity >= 2 ? 1.5 : 0.8;
        ctx.beginPath(); ctx.moveTo(wellCenterX - halfW1, y1); ctx.lineTo(wellCenterX - halfW2, y2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(wellCenterX + halfW1, y1); ctx.lineTo(wellCenterX + halfW2, y2); ctx.stroke();

        if (pt.washoutRate > 5) {
          ctx.strokeStyle = 'rgba(0,0,0,0.15)'; ctx.lineWidth = 0.5; ctx.setLineDash([2, 3]);
          ctx.beginPath(); ctx.moveTo(wellCenterX - halfBit1, y1); ctx.lineTo(wellCenterX - halfBit1, y2); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(wellCenterX + halfBit1, y1); ctx.lineTo(wellCenterX + halfBit1, y2); ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      CASING_PROGRAM.forEach((csg) => {
        const csgTop = Math.max(vTop, 0);
        // Cap casing visualization at KOP — below that the well is no longer vertical,
        // so a TVD-axis vertical bar would mislead.
        const csgBot = Math.min(csg.bottom, currentTVD, verticalCap);
        if (csgBot <= csgTop) return;
        const y1 = scaleY(csgTop);
        const y2 = scaleY(csgBot);
        const halfOD = (csg.od / 26) * maxWellPx / 2;
        const wallThick = 2;

        ctx.fillStyle = csg.color;
        ctx.globalAlpha = 0.6;
        ctx.fillRect(wellCenterX - halfOD - wallThick, y1, wallThick, y2 - y1);
        ctx.fillRect(wellCenterX + halfOD, y1, wallThick, y2 - y1);
        ctx.globalAlpha = 1;

        ctx.fillStyle = TH.casingInner;
        ctx.globalAlpha = 0.3;
        ctx.fillRect(wellCenterX - halfOD, y1, halfOD * 2, y2 - y1);
        ctx.globalAlpha = 1;
      });

      anomalies.forEach((a) => {
        const midDepth = (a.top + a.bottom) / 2;
        if (midDepth < vTop || midDepth > vBottom || midDepth > currentTVD) return;
        // Caliper anomalies are a vertical-hole concept; suppress labels below KOP
        if (midDepth > verticalCap) return;
        const y = scaleY(midDepth);
        const severityColors = ['#D4A017', '#D4770C', '#C0392B'];
        const severityLabels = ['轻度扩径', '中度扩径', '严重扩径'];
        const color = severityColors[Math.min(a.severity - 1, 2)];
        const label = severityLabels[Math.min(a.severity - 1, 2)];

        ctx.fillStyle = color; ctx.font = 'bold 9px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
        ctx.fillText(`● ${label} ${a.maxRate.toFixed(0)}%`, wellCenterX + maxWellPx / 2 + 14, y + 3);

        ctx.strokeStyle = color; ctx.lineWidth = 0.5; ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.moveTo(wellCenterX + maxWellPx / 2 + 2, y);
        ctx.lineTo(wellCenterX + maxWellPx / 2 + 12, y);
        ctx.stroke(); ctx.setLineDash([]);
      });

    }

    // ── Trajectory polyline (true (lateral, TVD) path) ──
    if (trajectory.length > 1 && pxPerMeter > 0) {
      const visStations = trajectory.filter((s) => s.md <= currentDepth);
      if (visStations.length > 1) {
        const stationPx = visStations.map((s) => ({
          md: s.md, tvd: s.tvd,
          x: wellCenterX + horizOf(s) * pxPerMeter,
          y: scaleY(s.tvd),
        }));

        // Outline pipe
        ctx.strokeStyle = TH.casing; ctx.lineWidth = 6; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.globalAlpha = 0.35;
        ctx.beginPath();
        stationPx.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Inner bore
        ctx.strokeStyle = TH.accent; ctx.lineWidth = 2.2;
        ctx.beginPath();
        stationPx.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.stroke();
        ctx.lineCap = 'butt'; ctx.lineJoin = 'miter';

        // Bit at last station
        const tip = stationPx[stationPx.length - 1];
        ctx.fillStyle = '#8B0000';
        ctx.beginPath(); ctx.arc(tip.x, tip.y, 5, 0, Math.PI * 2); ctx.fill();

        // Label with background so it stays readable when overlapping zone labels
        ctx.font = 'bold 11px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace';
        const label = `MD ${Math.round(tip.md)} · TVD ${Math.round(tip.tvd)}`;
        const tw = ctx.measureText(label).width;
        const lx = tip.x + 10, ly = tip.y + 16; // place BELOW the tip to dodge zone labels above
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(lx - 3, ly - 11, tw + 6, 15);
        ctx.strokeStyle = TH.accent; ctx.lineWidth = 1;
        ctx.strokeRect(lx - 3, ly - 11, tw + 6, 15);
        ctx.fillStyle = TH.accent;
        ctx.fillText(label, lx, ly);
      }
    } else if (visibleCaliper.length) {
      // Fallback: vertical well, bit at last caliper depth
      const lastCal = visibleCaliper[visibleCaliper.length - 1];
      const bitY = scaleY(lastCal.depth);
      const bitHalfW = (lastCal.bitSize / 26) * maxWellPx / 2;
      ctx.fillStyle = '#8B0000';
      ctx.beginPath();
      ctx.moveTo(wellCenterX - bitHalfW * 0.6, bitY - 4);
      ctx.lineTo(wellCenterX - bitHalfW, bitY + 4);
      ctx.lineTo(wellCenterX + bitHalfW, bitY + 4);
      ctx.lineTo(wellCenterX + bitHalfW * 0.6, bitY - 4);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = TH.accent; ctx.font = 'bold 11px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace';
      ctx.fillText(`${Math.round(lastCal.depth)}m`, wellCenterX + bitHalfW + 14, bitY + 5);
    }

    ctx.fillStyle = TH.textMuted; ctx.font = '10px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace';
    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
    const step = vBottom - vTop > 2000 ? 500 : vBottom - vTop > 800 ? 200 : 100;
    for (let d = Math.ceil(vTop / step) * step; d <= vBottom; d += step) {
      const y = scaleY(d); ctx.fillText(`${d}`, 4, y + 3);
      ctx.beginPath(); ctx.moveTo(trackLeft, y); ctx.lineTo(trackLeft + trackWidth, y); ctx.stroke();
    }
    ctx.save(); ctx.fillStyle = TH.textMuted; ctx.font = '10px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
    ctx.translate(12, H / 2); ctx.rotate(-Math.PI / 2); ctx.fillText('垂深 TVD (m)', -32, 0); ctx.restore();

  }, [currentDepth, currentTVD, viewRange, layers, zones, caliperData, anomalies, trajectory]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// ── Pressure Panel ──────────
function PressurePanel({ currentDepth, viewRange, pressureData, anomalies, trajectory }: PanelProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr; canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    const [vTop, vBottom] = viewRange;
    const scaleY = (d: number) => ((d - vTop) / (vBottom - vTop)) * H;
    const margin = { l: 50, r: 15 }, plotW = W - margin.l - margin.r;

    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);

    // MD → TVD interpolation (for dual axis annotation)
    const mdToTvd = (md: number): number => {
      if (!trajectory.length) return md;
      if (md <= trajectory[0].md) return trajectory[0].tvd;
      const last = trajectory[trajectory.length - 1];
      if (md >= last.md) return last.tvd;
      for (let i = 1; i < trajectory.length; i++) {
        if (trajectory[i].md >= md) {
          const a = trajectory[i - 1], b = trajectory[i];
          const t = b.md === a.md ? 0 : (md - a.md) / (b.md - a.md);
          return a.tvd + t * (b.tvd - a.tvd);
        }
      }
      return last.tvd;
    };

    anomalies.forEach((a) => {
      if (a.bottom < vTop || a.top > vBottom) return;
      if (a.top > currentDepth) return;
      const y1 = scaleY(Math.max(a.top, vTop));
      const y2 = scaleY(Math.min(a.bottom, vBottom, currentDepth));
      const severityColors = ['rgba(212,160,23,0.08)', 'rgba(212,119,12,0.12)', 'rgba(192,57,43,0.15)'];
      ctx.fillStyle = severityColors[Math.min(a.severity - 1, 2)];
      ctx.fillRect(margin.l, y1, plotW, y2 - y1);

      const borderColors = ['#D4A017', '#D4770C', '#C0392B'];
      ctx.strokeStyle = borderColors[Math.min(a.severity - 1, 2)];
      ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(margin.l, y1); ctx.lineTo(margin.l + plotW, y1); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(margin.l, y2); ctx.lineTo(margin.l + plotW, y2); ctx.stroke();
      ctx.setLineDash([]);

      const labels = ['轻度异常', '中度异常', '严重异常'];
      ctx.fillStyle = borderColors[Math.min(a.severity - 1, 2)];
      ctx.font = 'bold 9px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
      ctx.fillText(`⚠ ${labels[Math.min(a.severity - 1, 2)]} (扩径${a.maxRate.toFixed(0)}%)`, margin.l + plotW - 130, y1 + 11);
    });

    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
    for (let p = 0; p <= 90; p += 10) {
      const x = margin.l + (p / 90) * plotW;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      if (p % 20 === 0) { ctx.fillStyle = TH.textMuted; ctx.font = '9px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace'; ctx.fillText(`${p}`, x - 6, H - 4); }
    }

    const visibleData = pressureData.filter((d) => d.depth >= vTop && d.depth <= vBottom && d.depth <= currentDepth);
    const curves = [
      { key: 'pore' as const, color: TH.pore, label: '孔隙压力' },
      { key: 'collapse' as const, color: TH.collapse, label: '坍塌压力' },
      { key: 'fracture' as const, color: TH.fracture, label: '破裂压力' },
    ];

    if (visibleData.length > 2) {
      ctx.fillStyle = 'rgba(40,116,166,0.04)'; ctx.beginPath();
      visibleData.forEach((d, i) => { const x = margin.l + (d.collapse / 90) * plotW; i === 0 ? ctx.moveTo(x, scaleY(d.depth)) : ctx.lineTo(x, scaleY(d.depth)); });
      for (let i = visibleData.length - 1; i >= 0; i--) { ctx.lineTo(margin.l + (visibleData[i].fracture / 90) * plotW, scaleY(visibleData[i].depth)); }
      ctx.closePath(); ctx.fill();
    }

    curves.forEach((curve) => {
      if (visibleData.length < 2) return;
      ctx.beginPath(); ctx.strokeStyle = curve.color; ctx.lineWidth = 2;
      visibleData.forEach((d, i) => { const x = margin.l + (d[curve.key] / 90) * plotW; i === 0 ? ctx.moveTo(x, scaleY(d.depth)) : ctx.lineTo(x, scaleY(d.depth)); });
      ctx.stroke();
    });

    curves.forEach((curve, i) => { ctx.fillStyle = curve.color; ctx.fillRect(margin.l + 8 + i * 85, 8, 14, 3); ctx.font = '10px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif'; ctx.fillText(curve.label, margin.l + 26 + i * 85, 14); });

    // Depth axis: MD (measured depth) with corresponding TVD beneath
    const step = vBottom - vTop > 2000 ? 500 : vBottom - vTop > 800 ? 200 : 100;
    for (let d = Math.ceil(vTop / step) * step; d <= vBottom; d += step) {
      const y = scaleY(d);
      ctx.fillStyle = TH.textSec; ctx.font = '10px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace';
      ctx.fillText(`${d}`, 4, y + 1);
      ctx.fillStyle = TH.textMuted; ctx.font = '8px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace';
      ctx.fillText(`↧${Math.round(mdToTvd(d))}`, 4, y + 11);
    }
    // Axis title (top-left): MD / TVD legend
    ctx.fillStyle = TH.textSec; ctx.font = 'bold 9px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
    ctx.fillText('测深MD', 4, 14);
    ctx.fillStyle = TH.textMuted; ctx.font = '8px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
    ctx.fillText('↧垂深TVD', 4, 24);

    // Current bit position — by MD (drilling progress)
    if (currentDepth >= vTop && currentDepth <= vBottom) {
      const cy = scaleY(currentDepth);
      ctx.strokeStyle = TH.accent; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(margin.l, cy); ctx.lineTo(W - margin.r, cy); ctx.stroke(); ctx.setLineDash([]);
    }

    ctx.fillStyle = TH.textMuted; ctx.font = '10px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif'; ctx.fillText('压力 (MPa)', W / 2 - 25, H - 18);
  }, [currentDepth, viewRange, pressureData, anomalies, trajectory]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// ── Caliper Track ─────────────────
function CaliperPanel({ currentTVD, viewRange, caliperData }: PanelProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr; canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    const [vTop, vBottom] = viewRange;
    const scaleY = (d: number) => ((d - vTop) / (vBottom - vTop)) * H;
    const margin = { l: 8, r: 8 }, plotW = W - margin.l - margin.r;

    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
    [0, 10, 20, 30, 40, 50].forEach((v) => {
      const x = margin.l + (v / 50) * plotW;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      if (v % 20 === 0) { ctx.fillStyle = TH.textMuted; ctx.font = '8px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace'; ctx.fillText(`${v}%`, x, H - 3); }
    });

    const threshX = margin.l + (10 / 50) * plotW;
    ctx.strokeStyle = 'rgba(192,57,43,0.3)'; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(threshX, 0); ctx.lineTo(threshX, H); ctx.stroke(); ctx.setLineDash([]);

    const visible = caliperData.filter((c) => c.depth >= vTop && c.depth <= vBottom && c.depth <= currentTVD);
    if (visible.length > 1) {
      ctx.beginPath();
      visible.forEach((pt, i) => {
        const x = margin.l + (Math.max(0, pt.washoutRate) / 50) * plotW;
        const y = scaleY(pt.depth);
        i === 0 ? ctx.moveTo(margin.l, y) : ctx.lineTo(x, y);
      });
      ctx.lineTo(margin.l, scaleY(visible[visible.length - 1].depth));
      ctx.closePath();
      ctx.fillStyle = 'rgba(212,160,23,0.1)';
      ctx.fill();

      ctx.beginPath();
      visible.forEach((pt, i) => {
        const x = margin.l + (Math.max(0, pt.washoutRate) / 50) * plotW;
        const y = scaleY(pt.depth);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = '#B8860B'; ctx.lineWidth = 1.5; ctx.stroke();

      visible.forEach((pt) => {
        if (pt.washoutRate > 20) {
          const x = margin.l + (pt.washoutRate / 50) * plotW;
          const y = scaleY(pt.depth);
          ctx.beginPath(); ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fillStyle = pt.washoutSeverity >= 3 ? TH.washoutSevere : TH.washoutMod;
          ctx.fill();
        }
      });
    }
  }, [currentTVD, viewRange, caliperData]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// ── Main Page ──────────────────────────────────
export default function FormationPage({ initialView3D = false }: { initialView3D?: boolean } = {}) {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;

  const [layers, setLayers] = useState<Layer[]>([]);
  const [zones, setZones] = useState<AvoidanceZone[]>([]);
  const [trajectory, setTrajectory] = useState<Station[]>([]);
  const [pressureData, setPressureData] = useState<PressurePoint[]>([]);
  // 地质建模: 天然裂缝 / 断层面 / 层位面 (3D 视图用, 失败不阻塞主流程)
  const [fractures, setFractures] = useState<any[]>([]);
  const [faults, setFaults] = useState<any[]>([]);
  const [horizons, setHorizons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentDepth, setCurrentDepth] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [viewMode, setViewMode] = useState<'full' | 'target' | 'washout'>('full');
  const [customRange, setCustomRange] = useState<[number, number] | null>(null);
  const [view3D, setView3D] = useState(initialView3D);
  const animRef = useRef<number | null>(null);
  const panelsRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ y: number; vTop: number; vBottom: number; h: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const unwrap = (data: any) => (Array.isArray(data) ? data : data?.results ?? []);
    Promise.all([
      formationAPI.getLayers(projectId),
      wellboreAPI.getSurvey(projectId),
      pressureAPI.getProfile(projectId),
      wellboreAPI.getAvoidanceZones(projectId),
    ])
      .then(([layersRes, surveyRes, pressureRes, avoidanceRes]) => {
        if (cancelled) return;
        setLayers(mapLayers(unwrap(layersRes.data)));
        setTrajectory(mapSurvey(unwrap(surveyRes.data)));
        setPressureData(mapPressure(unwrap(pressureRes.data)));
        setZones(mapAvoidance(unwrap(avoidanceRes.data)));
      })
      .catch((e) => { if (!cancelled) setError(e?.message || '加载数据失败'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // 天然裂缝/断层/层位: 后端未迁移时会 404, 静默降级为空数组
    formationAPI.listFractures(projectId)
      .then(({ data }) => { if (!cancelled) setFractures(unwrap(data)); }).catch(() => {});
    formationAPI.listFaults(projectId)
      .then(({ data }) => { if (!cancelled) setFaults(unwrap(data)); }).catch(() => {});
    formationAPI.listHorizons(projectId)
      .then(({ data }) => { if (!cancelled) setHorizons(unwrap(data)); }).catch(() => {});

    return () => { cancelled = true; };
  }, [projectId]);

  // maxDepth = TVD-axis 范围 (地层/画布 Y 轴)
  const maxDepth = useMemo(() => {
    const candidates = [
      ...layers.map((l) => l.bottom),
      ...trajectory.map((s) => s.tvd),
      currentProject?.total_depth ?? 0,
    ];
    const m = Math.max(0, ...candidates);
    return m > 0 ? Math.ceil(m / 100) * 100 + 200 : 4500;
  }, [layers, trajectory, currentProject]);

  // maxMD = 钻进进度上限 (井底测深). 无测斜时退化为 maxDepth.
  const maxMD = useMemo(() => {
    const md = trajectory.length ? trajectory[trajectory.length - 1].md : 0;
    return md > 0 ? md : maxDepth;
  }, [trajectory, maxDepth]);

  const caliperData = useMemo(
    () => generateCaliperData(layers, zones, maxDepth),
    [layers, zones, maxDepth]
  );
  const anomalies = useMemo(() => detectAnomalies(caliperData), [caliperData]);

  const targetLayer = useMemo(() => layers.find((l) => l.target), [layers]);
  const viewRange: [number, number] = useMemo(() => {
    if (customRange) return customRange;
    if (viewMode === 'full') return [0, maxDepth];
    if (viewMode === 'target' && targetLayer) {
      return [Math.max(0, targetLayer.top - 500), targetLayer.bottom + 320];
    }
    if (viewMode === 'washout' && anomalies.length) {
      const top = Math.min(...anomalies.map((a) => a.top));
      const bot = Math.max(...anomalies.map((a) => a.bottom));
      return [Math.max(0, top - 200), bot + 200];
    }
    return [0, maxDepth];
  }, [viewMode, maxDepth, targetLayer, anomalies, customRange]);

  // ── Clamp range to [0, maxDepth], enforce minimum span ──
  const clampRange = (top: number, bottom: number): [number, number] => {
    const minSpan = 100;
    if (bottom - top < minSpan) bottom = top + minSpan;
    if (top < 0) { bottom -= top; top = 0; }
    if (bottom > maxDepth) {
      const shift = bottom - maxDepth;
      bottom = maxDepth;
      top = Math.max(0, top - shift);
    }
    return [top, bottom];
  };

  // ── Wheel: pan (default) / zoom (with shift or ctrl) ──
  useEffect(() => {
    const el = panelsRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const [vTop, vBottom] = viewRange;
      const span = vBottom - vTop;
      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        // Zoom — anchor at mouse Y
        const rect = el.getBoundingClientRect();
        const frac = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
        const anchorDepth = vTop + span * frac;
        const factor = e.deltaY > 0 ? 1.2 : 1 / 1.2;
        const newSpan = span * factor;
        setCustomRange(clampRange(anchorDepth - newSpan * frac, anchorDepth + newSpan * (1 - frac)));
      } else {
        // Pan
        const delta = (e.deltaY / 400) * span;
        setCustomRange(clampRange(vTop + delta, vBottom + delta));
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [viewRange, maxDepth]);

  // ── Drag to pan ──
  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    const el = panelsRef.current;
    if (!el) return;
    const [vTop, vBottom] = viewRange;
    dragRef.current = { y: e.clientY, vTop, vBottom, h: el.getBoundingClientRect().height };
  };
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const { y, vTop, vBottom, h } = dragRef.current;
      const span = vBottom - vTop;
      const delta = -((e.clientY - y) / h) * span;
      setCustomRange(clampRange(vTop + delta, vBottom + delta));
    };
    const onUp = () => { dragRef.current = null; setDragging(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [maxDepth]);

  useEffect(() => {
    if (isPlaying) {
      const tick = () => {
        setCurrentDepth((d) => { const n = d + speed * 8; if (n >= maxMD) { setIsPlaying(false); return maxMD; } return n; });
        animRef.current = requestAnimationFrame(tick);
      };
      animRef.current = requestAnimationFrame(tick);
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [isPlaying, speed, maxMD]);

  const reset = () => { setCurrentDepth(0); setIsPlaying(false); };

  // currentDepth = 钻进进度 (测深 MD). 钻头真垂深 currentTVD 从轨迹按 MD 插值得到.
  // 三压力/井径/地层等都是 TVD 的函数, 必须用 currentTVD 裁剪, 不能用 MD.
  const currentTVD = useMemo(() => {
    if (!trajectory.length) return currentDepth; // 无测斜数据时退化为 MD≈TVD
    if (currentDepth <= trajectory[0].md) return trajectory[0].tvd;
    const last = trajectory[trajectory.length - 1];
    if (currentDepth >= last.md) return last.tvd;
    for (let i = 1; i < trajectory.length; i++) {
      if (trajectory[i].md >= currentDepth) {
        const a = trajectory[i - 1], b = trajectory[i];
        const t = b.md === a.md ? 0 : (currentDepth - a.md) / (b.md - a.md);
        return a.tvd + t * (b.tvd - a.tvd);
      }
    }
    return last.tvd;
  }, [trajectory, currentDepth]);

  // Use MD for filtering trajectory so horizontal wells behave correctly
  const currentStation = trajectory.filter((s) => s.md <= currentDepth).pop();
  // 地层/井径/避障判断都按 TVD
  const currentLayer = layers.find((l) => currentTVD >= l.top && currentTVD < l.bottom);
  const currentCal = caliperData.find((c) => Math.abs(c.depth - currentTVD) < 15);
  const inTarget = !!(targetLayer && currentTVD >= targetLayer.top && currentTVD <= targetLayer.bottom);
  const inDanger = zones.some((z) => currentTVD >= z.top && currentTVD <= z.bottom);
  const isWashout = !!(currentCal && currentCal.washoutRate > 10);

  const btnS = (color: string, active: boolean) => ({
    width: 28, height: 28, borderRadius: 4, cursor: 'pointer', fontSize: 12,
    fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${active ? color : '#D6DAE0'}`, background: active ? `${color}14` : '#FFFFFF', color: active ? color : '#8598AD',
  });

  if (!projectId) {
    return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;
  }
  if (error) {
    return <div style={{ padding: 40, color: TH.washoutSevere }}>加载失败: {error}</div>;
  }
  // 不再阻塞性"加载数据中…"; 数据未到位时短暂空白

  const panelProps: PanelProps = { currentDepth, currentTVD, viewRange, layers, zones, caliperData, anomalies, pressureData, trajectory };

  return (
    <div style={{ height: 'calc(100vh)', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: TH.headerBg, borderBottom: `1px solid ${TH.headerBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>地层模型 · 井筒 · 压力</h2>
          <span style={{
            fontSize: 11, padding: '2px 10px', borderRadius: 4, fontWeight: 600,
            background: isWashout ? '#FDECEA' : inTarget ? TH.accentLight : inDanger ? '#FFF3E0' : '#F0F3F6',
            color: isWashout ? TH.washoutSevere : inTarget ? TH.accent : inDanger ? '#D4770C' : TH.textSec,
            border: `1px solid ${isWashout ? 'rgba(192,57,43,0.2)' : inTarget ? 'rgba(194,83,58,0.2)' : '#E4EAF0'}`,
          }}>
            {isWashout ? `⚠ 扩径 ${currentCal!.washoutRate.toFixed(1)}%` : inTarget ? '★ 目标层段' : inDanger ? '⚠ 避障区域' : currentLayer?.name || '地表'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => setIsPlaying(!isPlaying)} style={btnS(isPlaying ? '#C0392B' : '#27864A', true)}>{isPlaying ? '⏸' : '▶'}</button>
          <button onClick={reset} style={btnS('#2874A6', false)}>↺</button>
          {[1, 2, 4].map((s) => (<button key={s} onClick={() => setSpeed(s)} style={{ ...btnS(TH.accent, speed === s), fontSize: 10 }}>{s}x</button>))}
          <div style={{ width: 160, height: 5, background: '#ECEEF1', borderRadius: 3, marginLeft: 8, cursor: 'pointer', border: '1px solid #D6DAE0' }}
            onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setCurrentDepth(Math.min(maxMD, Math.max(0, ((e.clientX - r.left) / r.width) * maxMD))); }}>
            <div style={{ height: '100%', width: `${Math.min(100, (currentDepth / maxMD) * 100)}%`, background: 'linear-gradient(90deg, #2874A6, #C2533A)', borderRadius: 3 }} />
          </div>
          <span style={{ fontSize: 12, color: TH.textSec, fontFamily: 'monospace', minWidth: 96 }}>MD {Math.round(currentDepth)} m</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 4,
            background: customRange ? TH.accentLight : '#F0F3F6',
            color: customRange ? TH.accent : TH.textSec,
            border: `1px solid ${customRange ? 'rgba(194,83,58,0.25)' : '#E4EAF0'}`,
            fontFamily: 'monospace', fontWeight: 600,
          }} title={customRange ? '自由缩放 (滚轮/拖拽)' : '预设视图'}>
            {Math.round(viewRange[0])} – {Math.round(viewRange[1])} m
            <span style={{ marginLeft: 6, color: TH.textMuted, fontWeight: 400 }}>
              ({Math.round(viewRange[1] - viewRange[0])}m)
            </span>
          </span>
          {[{ key: 'full' as const, label: '全井段' }, { key: 'target' as const, label: '目标层' }, { key: 'washout' as const, label: '扩径段' }].map((v) => (
            <button key={v.key} onClick={() => { setViewMode(v.key); setCustomRange(null); }} style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
              border: `1px solid ${viewMode === v.key ? TH.accent : '#D6DAE0'}`,
              background: viewMode === v.key ? TH.accentLight : '#FFFFFF', color: viewMode === v.key ? TH.accent : TH.textSec,
            }}>{v.label}</button>
          ))}
          <button onClick={() => setView3D(!view3D)} style={{
            padding: '4px 12px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
            fontWeight: 700, marginLeft: 4,
            border: `1px solid ${view3D ? '#6A4C93' : '#D6DAE0'}`,
            background: view3D ? 'rgba(106,76,147,0.08)' : '#FFFFFF', color: view3D ? '#6A4C93' : TH.textSec,
          }}>{view3D ? '◧ 剖面' : '◰ 3D'}</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 1, background: TH.statBorder }}>
        {[
          { label: '测深', value: `${currentStation?.md || 0}`, unit: 'm' },
          { label: '垂深', value: `${Math.round(currentStation?.tvd || 0)}`, unit: 'm' },
          { label: '井斜', value: `${(currentStation?.inc || 0).toFixed(1)}`, unit: '°' },
          { label: '钻头', value: currentCal ? `${currentCal.bitSize}"` : '-', unit: '' },
          { label: '井径', value: currentCal ? `${currentCal.caliper.toFixed(1)}"` : '-', unit: '', alert: isWashout },
          { label: '扩径率', value: currentCal ? `${currentCal.washoutRate.toFixed(1)}` : '-', unit: '%', alert: isWashout },
          { label: '层位', value: currentLayer?.name?.slice(0, 5) || '-', unit: '' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: 1, padding: '6px 8px', textAlign: 'center',
            background: (s as any).alert ? '#FEF0EE' : TH.statBg,
            borderBottom: (s as any).alert ? '2px solid #C0392B' : '2px solid transparent',
          }}>
            <div style={{ fontSize: 9, color: TH.textMuted, marginBottom: 1 }}>{s.label}</div>
            <div style={{
              fontSize: 14, fontWeight: 700, fontFamily: 'monospace',
              color: (s as any).alert ? '#C0392B' : TH.text,
            }}>
              {s.value}<span style={{ fontSize: 9, fontWeight: 400, color: TH.textSec, marginLeft: 1 }}>{s.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {view3D ? (
        <div style={{ flex: 1, minHeight: 0, background: '#FAFBFC' }}>
          <Formation3DView
            layers={layers}
            trajectory={trajectory}
            zones={zones}
            caliperData={caliperData}
            casing={CASING_PROGRAM}
            currentDepth={currentDepth}
            fractures={fractures}
            faults={faults}
            horizons={horizons}
          />
        </div>
      ) : (
      <div
        ref={panelsRef}
        onMouseDown={onMouseDown}
        title="滚轮平移 · Shift+滚轮缩放 · 拖拽平移"
        style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 100px 1fr', gap: 1, minHeight: 0, background: TH.statBorder, cursor: dragging ? 'grabbing' : 'grab' }}
      >
        <div style={{ background: TH.canvasBg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: TH.accent, background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: TH.accent }} />
            地层剖面 + 井筒
          </div>
          <div style={{ flex: 1 }}><FormationPanel {...panelProps} /></div>
        </div>

        <div style={{ background: TH.canvasBg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '4px 6px', fontSize: 10, fontWeight: 600, color: '#B8860B', background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}`, textAlign: 'center' }}>
            扩径率
          </div>
          <div style={{ flex: 1 }}><CaliperPanel {...panelProps} /></div>
        </div>

        <div style={{ background: TH.canvasBg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: TH.fracture, background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: TH.fracture }} />
            三压力剖面
            {anomalies.some(a => a.top <= currentTVD && a.severity >= 2) && (
              <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: '#FDECEA', color: '#C0392B', marginLeft: 4 }}>
                异常联动
              </span>
            )}
          </div>
          <div style={{ flex: 1 }}><PressurePanel {...panelProps} /></div>
        </div>
      </div>
      )}
    </div>
  );
}
